import {
  Ruler, Weight, Thermometer, Beaker, Square, Gauge, HardDrive, Clock,
  Calculator, Percent, HeartPulse, Cake, KeyRound, Type, Hash, CaseSensitive,
} from "lucide-react";

// ---------- UNIT CONVERTERS ----------
// factor = how many BASE units one of this unit equals
export const converterCategories = [
  {
    id: "length",
    label: "Length",
    icon: Ruler,
    base: "meter",
    blurb: "Convert distances between metric and imperial units.",
    units: [
      { id: "kilometers", name: "Kilometers", symbol: "km", factor: 1000 },
      { id: "meters", name: "Meters", symbol: "m", factor: 1 },
      { id: "centimeters", name: "Centimeters", symbol: "cm", factor: 0.01 },
      { id: "millimeters", name: "Millimeters", symbol: "mm", factor: 0.001 },
      { id: "miles", name: "Miles", symbol: "mi", factor: 1609.344 },
      { id: "yards", name: "Yards", symbol: "yd", factor: 0.9144 },
      { id: "feet", name: "Feet", symbol: "ft", factor: 0.3048 },
      { id: "inches", name: "Inches", symbol: "in", factor: 0.0254 },
      { id: "nautical-miles", name: "Nautical Miles", symbol: "nmi", factor: 1852 },
    ],
  },
  {
    id: "weight",
    label: "Weight",
    icon: Weight,
    base: "gram",
    blurb: "Convert mass and weight across systems.",
    units: [
      { id: "tonnes", name: "Metric Tonnes", symbol: "t", factor: 1_000_000 },
      { id: "kilograms", name: "Kilograms", symbol: "kg", factor: 1000 },
      { id: "grams", name: "Grams", symbol: "g", factor: 1 },
      { id: "milligrams", name: "Milligrams", symbol: "mg", factor: 0.001 },
      { id: "pounds", name: "Pounds", symbol: "lb", factor: 453.59237 },
      { id: "ounces", name: "Ounces", symbol: "oz", factor: 28.349523 },
      { id: "stones", name: "Stones", symbol: "st", factor: 6350.29318 },
    ],
  },
  {
    id: "temperature",
    label: "Temperature",
    icon: Thermometer,
    base: "celsius",
    blurb: "Convert between Celsius, Fahrenheit and Kelvin.",
    special: true,
    units: [
      { id: "celsius", name: "Celsius", symbol: "°C" },
      { id: "fahrenheit", name: "Fahrenheit", symbol: "°F" },
      { id: "kelvin", name: "Kelvin", symbol: "K" },
    ],
  },
  {
    id: "volume",
    label: "Volume",
    icon: Beaker,
    base: "liter",
    blurb: "Convert liquid and dry volume measures.",
    units: [
      { id: "cubic-meters", name: "Cubic Meters", symbol: "m³", factor: 1000 },
      { id: "liters", name: "Liters", symbol: "L", factor: 1 },
      { id: "milliliters", name: "Milliliters", symbol: "mL", factor: 0.001 },
      { id: "gallons", name: "US Gallons", symbol: "gal", factor: 3.785411 },
      { id: "quarts", name: "US Quarts", symbol: "qt", factor: 0.946353 },
      { id: "pints", name: "US Pints", symbol: "pt", factor: 0.473176 },
      { id: "cups", name: "US Cups", symbol: "cup", factor: 0.236588 },
      { id: "fluid-ounces", name: "Fluid Ounces", symbol: "fl oz", factor: 0.0295735 },
    ],
  },
  {
    id: "area",
    label: "Area",
    icon: Square,
    base: "square-meter",
    blurb: "Convert land and surface area units.",
    units: [
      { id: "square-kilometers", name: "Square Kilometers", symbol: "km²", factor: 1_000_000 },
      { id: "hectares", name: "Hectares", symbol: "ha", factor: 10_000 },
      { id: "square-meters", name: "Square Meters", symbol: "m²", factor: 1 },
      { id: "square-feet", name: "Square Feet", symbol: "ft²", factor: 0.092903 },
      { id: "square-miles", name: "Square Miles", symbol: "mi²", factor: 2_589_988.11 },
      { id: "acres", name: "Acres", symbol: "ac", factor: 4046.8564 },
    ],
  },
  {
    id: "speed",
    label: "Speed",
    icon: Gauge,
    base: "meter-per-second",
    blurb: "Convert velocity between common units.",
    units: [
      { id: "kilometers-per-hour", name: "Kilometers / Hour", symbol: "km/h", factor: 0.277778 },
      { id: "miles-per-hour", name: "Miles / Hour", symbol: "mph", factor: 0.44704 },
      { id: "meters-per-second", name: "Meters / Second", symbol: "m/s", factor: 1 },
      { id: "knots", name: "Knots", symbol: "kn", factor: 0.514444 },
      { id: "feet-per-second", name: "Feet / Second", symbol: "ft/s", factor: 0.3048 },
    ],
  },
  {
    id: "data",
    label: "Digital Storage",
    icon: HardDrive,
    base: "byte",
    blurb: "Convert bytes, bits and binary storage sizes.",
    units: [
      { id: "terabytes", name: "Terabytes", symbol: "TB", factor: 1_099_511_627_776 },
      { id: "gigabytes", name: "Gigabytes", symbol: "GB", factor: 1_073_741_824 },
      { id: "megabytes", name: "Megabytes", symbol: "MB", factor: 1_048_576 },
      { id: "kilobytes", name: "Kilobytes", symbol: "KB", factor: 1024 },
      { id: "bytes", name: "Bytes", symbol: "B", factor: 1 },
      { id: "bits", name: "Bits", symbol: "bit", factor: 0.125 },
    ],
  },
  {
    id: "time",
    label: "Time",
    icon: Clock,
    base: "second",
    blurb: "Convert seconds, minutes, hours, days and more.",
    units: [
      { id: "weeks", name: "Weeks", symbol: "wk", factor: 604800 },
      { id: "days", name: "Days", symbol: "d", factor: 86400 },
      { id: "hours", name: "Hours", symbol: "h", factor: 3600 },
      { id: "minutes", name: "Minutes", symbol: "min", factor: 60 },
      { id: "seconds", name: "Seconds", symbol: "s", factor: 1 },
      { id: "milliseconds", name: "Milliseconds", symbol: "ms", factor: 0.001 },
    ],
  },
];

export const calculators = [
  { slug: "bmi", title: "BMI Calculator", icon: HeartPulse, blurb: "Body Mass Index from height & weight." },
  { slug: "percentage", title: "Percentage Calculator", icon: Percent, blurb: "Percentages, increases & differences." },
  { slug: "loan", title: "Loan / EMI Calculator", icon: Calculator, blurb: "Monthly payment, interest & totals." },
  { slug: "age", title: "Age Calculator", icon: Cake, blurb: "Exact age in years, months & days." },
];

export const textTools = [
  { slug: "word-counter", title: "Word Counter", icon: Hash, blurb: "Words, characters, sentences & read time." },
  { slug: "case-converter", title: "Case Converter", icon: CaseSensitive, blurb: "UPPER, lower, Title & sentence case." },
  { slug: "password-generator", title: "Password Generator", icon: KeyRound, blurb: "Strong, customizable random passwords." },
  { slug: "lorem-ipsum", title: "Lorem Ipsum Generator", icon: Type, blurb: "Placeholder copy for mockups." },
];

export const getCategory = (id) => converterCategories.find((c) => c.id === id);
export const getUnit = (cat, id) => cat?.units.find((u) => u.id === id);
